import { useCallback, useContext, useEffect, useSyncExternalStore } from 'react';

import { attachWsVisibility, HIDDEN_TEARDOWN_GRACE_MS, isBackgroundTab } from 'app/(trade)/stores/wsVisibility';

import { PageActivityContext } from './pageActivityContext';

/**
 * Exported so a surface that PAUSES subscriptions (`CoveredEventContent`) can tell whether the
 * expansion below could have happened yet — covering the page before it has nothing to tear down.
 */
export const EXPAND_SUBSCRIPTIONS_DELAY_MS = 2000;
const EXPAND_SUBSCRIPTIONS_IDLE_TIMEOUT_MS = 2000;

/**
 * ONE `visibilitychange` listener for the whole app, attached lazily on the first consumer. Every
 * market card calls `useIsPageVisible`, so a listener per hook instance would put one DOM listener
 * (and one callback per tab switch) on the page per card.
 *
 * Delegates to the shared `attachWsVisibility` so this gate matches the other WS-subscription
 * owners (`bookStore`, `marginTickerStore`) — including its teardown grace period, so alt-tabbing
 * away for a moment doesn't unwatch every card's ticker and pay a full initial-snapshot burst on
 * refocus.
 */
const visibilityListeners = new Set<() => void>();
let isPageVisible = true;
let visibilityAttached = false;

function setPageVisible(next: boolean): void {
    if (isPageVisible === next) return;
    isPageVisible = next;
    for (const listener of visibilityListeners) listener();
}

function subscribeToPageVisibility(listener: () => void): () => void {
    if (!visibilityAttached) {
        visibilityAttached = true;
        isPageVisible = !isBackgroundTab();
        attachWsVisibility(
            { onHidden: () => setPageVisible(false), onVisible: () => setPageVisible(true) },
            { gracePeriodMs: HIDDEN_TEARDOWN_GRACE_MS },
        );
    }
    visibilityListeners.add(listener);
    return () => visibilityListeners.delete(listener);
}

function getIsPageVisible(): boolean {
    return isPageVisible;
}

function getIsPageVisibleOnServer(): boolean {
    return true;
}

/**
 * Whether the tab and this UI subtree are currently active. SSR-safe (server snapshot is `true`), so callers can gate
 * a subscription on it without splitting their render between server and client.
 */
export function useIsPageVisible(): boolean {
    const active = useContext(PageActivityContext);
    const visible = useSyncExternalStore(subscribeToPageVisibility, getIsPageVisible, getIsPageVisibleOnServer);
    return active && visible;
}

interface StagedKey {
    /** Refcount of mounted consumers; the entry is dropped when it reaches zero. */
    consumers: number;
    expanded: boolean;
    /** Cancels the not-yet-committed expansion timer. Cleared once `expanded` flips. */
    cancelPending?: () => void;
}

/**
 * Staging state is deliberately MODULE-level and shared per key, not per component. Every market
 * card on a page asks the same question — "is the critical-load window over for this event?" — and a
 * 150-market event holding 150 `setTimeout`s, 150 `requestIdleCallback`s and 150 pieces of component
 * state to answer it identically is exactly the boot cost staging exists to avoid. One timer per key
 * serves every consumer.
 *
 * One entry holds a key's whole state: an entry exists exactly while the key has consumers, so
 * "already armed or expanded" is a single lookup instead of an invariant spread across parallel maps.
 */
const stagedKeys = new Map<string, StagedKey>();
const expansionListeners = new Set<() => void>();

function notifyExpansionChange(): void {
    for (const listener of expansionListeners) listener();
}

function subscribeToExpansion(listener: () => void): () => void {
    expansionListeners.add(listener);
    return () => expansionListeners.delete(listener);
}

function acquireExpansion(key: string): void {
    const existing = stagedKeys.get(key);
    if (existing) {
        existing.consumers += 1;
        return;
    }

    const staged: StagedKey = { consumers: 1, expanded: false };
    stagedKeys.set(key, staged);

    let idleHandle: number | undefined;
    const commit = (): void => {
        staged.cancelPending = undefined;
        staged.expanded = true;
        notifyExpansionChange();
    };
    const timer = window.setTimeout(() => {
        if (typeof window.requestIdleCallback === 'function') {
            idleHandle = window.requestIdleCallback(commit, { timeout: EXPAND_SUBSCRIPTIONS_IDLE_TIMEOUT_MS });
            return;
        }
        commit();
    }, EXPAND_SUBSCRIPTIONS_DELAY_MS);

    staged.cancelPending = () => {
        window.clearTimeout(timer);
        if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
    };
}

function releaseExpansion(key: string): void {
    const staged = stagedKeys.get(key);
    if (!staged) return;

    staged.consumers -= 1;
    if (staged.consumers > 0) return;

    // Last consumer for this key left: cancel any pending timer and forget the expansion, so
    // navigating back to the same event re-arms the deferral instead of subscribing during its boot.
    staged.cancelPending?.();
    stagedKeys.delete(key);
}

function getHasExpandedOnServer(): boolean {
    return false;
}

/**
 * Whether the critical-load window for `key` has passed. Re-arms whenever `key` changes, so a
 * client-side navigation restarts the deferral for the new page.
 *
 * Extracted from the event page's old page-level subscription hook: live-market subscriptions are
 * now owned by individual cards, but the reason for staging them is unchanged — a large event
 * (a full prop slate, a 150-competitor field) that subscribes everything on mount lands an initial
 * snapshot per market inside the critical-load window. Cards combine this with `useIsPageVisible`
 * in their own subscription gate.
 */
export function useHasExpandedSubscriptions(key: string): boolean {
    useEffect(() => {
        if (!key) return;
        acquireExpansion(key);
        return () => releaseExpansion(key);
    }, [key]);

    // Memoized so React doesn't schedule a passive `updateStoreInstance` effect on every render of
    // every card just because the snapshot closure's identity changed.
    const getSnapshot = useCallback(() => stagedKeys.get(key)?.expanded ?? false, [key]);

    return useSyncExternalStore(subscribeToExpansion, getSnapshot, getHasExpandedOnServer);
}
